import React, { useState, useEffect, useMemo, useCallback } from "react";
import PermissionDetailsModal from "./PermissionDetailsModal";
import EditTrainerModal from "./EditTrainerModal";
import LoadingComponent from "./LoadingComponent";
import { getAuthHeaders } from "../Auth/authHelpers";

const AdminDashboard = ({ trainerID }) => {
	const API = process.env.REACT_APP_API_URL;

	const [permissionsData, setPermissionsData] = useState([]);
	const [sitesData, setSitesData] = useState([]);
	const [showSiteSelection, setShowSiteSelection] = useState(false);
	const [editingTrainer, setEditingTrainer] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [siteSearchTerm, setSiteSearchTerm] = useState("");

	const [newTrainerID, setNewTrainerID] = useState("");
	const [newTrainerName, setNewTrainerName] = useState("");
	const [newEtpAlias, setNewEtpAlias] = useState("");
	const [newBusinessTitle, setNewBusinessTitle] = useState("");
	const [newSiteNode, setNewSiteNode] = useState("");
	const [selectedSites, setSelectedSites] = useState([]);
	const [newPermissionLevel, setNewPermissionLevel] = useState("");

	const [selectedTrainerPermissions, setSelectedTrainerPermissions] = useState(
		[],
	);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const authHeaders = useCallback(
		async () => ({
			...(await getAuthHeaders(trainerID)),
			"Content-Type": "application/json",
		}),
		[trainerID],
	);

	const fetchJson = async (url, options = {}) => {
		const response = await fetch(url, options);

		const contentType = response.headers.get("content-type") || "";
		const isJson = contentType.includes("application/json");
		const body = isJson ? await response.json() : await response.text();

		if (!response.ok) {
			const message =
				typeof body === "string"
					? body
					: body?.error || body?.message || JSON.stringify(body);
			throw new Error(message);
		}

		return body;
	};

	const fetchPermissions = async () => {
		const data = await fetchJson(`${API}/permissions`, {
			headers: await authHeaders(),
		});
		setPermissionsData(Array.isArray(data) ? data : []);
	};

	const fetchSites = async () => {
		const data = await fetchJson(`${API}/sites`, {
			headers: await authHeaders(),
		});
		setSitesData(Array.isArray(data) ? data : []);
	};

	const refreshAll = async () => {
		setIsLoading(true);
		try {
			await Promise.all([fetchPermissions(), fetchSites()]);
		} catch (error) {
			console.error("Error fetching dashboard data:", error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (!trainerID || !API) return;
		refreshAll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trainerID, API]);

	const groupedPermissions = useMemo(() => {
		const grouped = {};

		for (const row of Array.isArray(permissionsData) ? permissionsData : []) {
			const trainerID = String(row?.TrainerID || "").trim();
			if (!trainerID) continue;

			const key = trainerID.toLowerCase();
			const permissionLevel = String(row?.PermissionLevel || "")
				.trim()
				.toLowerCase();
			const siteID = String(row?.SiteID || "").trim();

			if (!grouped[key]) {
				grouped[key] = {
					TrainerID: trainerID,
					user_name: String(row?.user_name || "").trim(),
					etp_alias: String(row?.etp_alias || "").trim(),
					site_node: String(row?.site_node || "").trim(),
					business_title: String(row?.business_title || "").trim(),
					permissions: new Set(),
					sites: [],
					siteKeys: new Set(),
				};
			}

			// prefer filled values if later rows have them
			if (!grouped[key].user_name && row?.user_name) {
				grouped[key].user_name = String(row.user_name).trim();
			}
			if (!grouped[key].etp_alias && row?.etp_alias) {
				grouped[key].etp_alias = String(row.etp_alias).trim();
			}
			if (!grouped[key].site_node && row?.site_node) {
				grouped[key].site_node = String(row.site_node).trim();
			}
			if (!grouped[key].business_title && row?.business_title) {
				grouped[key].business_title = String(row.business_title).trim();
			}

			if (permissionLevel) {
				grouped[key].permissions.add(permissionLevel);
			}

			const siteKey = `${siteID.toLowerCase()}|${permissionLevel}`;
			if (siteID && !grouped[key].siteKeys.has(siteKey)) {
				grouped[key].siteKeys.add(siteKey);
				grouped[key].sites.push({
					SiteID: siteID,
					PermissionLevel: row?.PermissionLevel || "",
					user_name: row?.user_name || "",
					etp_alias: row?.etp_alias || "",
					site_node: row?.site_node || "",
					business_title: row?.business_title || "",
				});
			}
		}

		return Object.values(grouped).map(({ siteKeys, ...rest }) => rest);
	}, [permissionsData]);

	const sortedPermissions = useMemo(() => {
		return [...groupedPermissions].sort((a, b) => {
			const aPerms = Array.from(a.permissions || []);
			const bPerms = Array.from(b.permissions || []);

			const aIsAdmin = aPerms.includes("admin");
			const bIsAdmin = bPerms.includes("admin");

			if (aIsAdmin && !bIsAdmin) return -1;
			if (!aIsAdmin && bIsAdmin) return 1;

			return String(a.user_name || a.TrainerID).localeCompare(
				String(b.user_name || b.TrainerID),
			);
		});
	}, [groupedPermissions]);

	const handleDeleteTrainer = async (targetTrainerID) => {
		if (
			!window.confirm(
				`Are you sure you want to delete all permissions for ${targetTrainerID}?`,
			)
		) {
			return;
		}

		try {
			await fetchJson(
				`${API}/permissions/${encodeURIComponent(targetTrainerID)}`,
				{
					method: "DELETE",
					headers: await authHeaders(),
				},
			);

			await refreshAll();
		} catch (error) {
			console.error("Error deleting trainer:", error);
			alert(`Delete failed: ${error.message}`);
		}
	};

	const handleEditClick = (targetTrainerID) => {
		const normalizedTarget = String(targetTrainerID || "")
			.trim()
			.toLowerCase();

		const trainer = sortedPermissions.find(
			(t) =>
				String(t.TrainerID || "")
					.trim()
					.toLowerCase() === normalizedTarget,
		);
		if (!trainer) return;

		const trainerPermissions = permissionsData.filter(
			(p) =>
				String(p.TrainerID || "")
					.trim()
					.toLowerCase() === normalizedTarget,
		);

		setEditingTrainer({
			TrainerID: trainer.TrainerID,
			user_name: trainer.user_name,
			PermissionLevel: trainerPermissions[0]?.PermissionLevel || "Trainer",
			etp_alias: trainer.etp_alias || "",
			site_node: trainer.site_node || "",
			business_title: trainer.business_title || "",
			selectedSiteIDs: [
				...new Set(
					trainerPermissions
						.map((p) => String(p.SiteID || "").trim())
						.filter(Boolean),
				),
			],
			permissions: trainerPermissions,
		});
	};

	const handleSaveChanges = async (updatedTrainer) => {
		try {
			const {
				TrainerID,
				selectedSiteIDs = [],
				PermissionLevel,
				user_name,
				etp_alias,
				site_node,
				business_title,
			} = updatedTrainer;

			await fetchJson(`${API}/permissions/${encodeURIComponent(TrainerID)}`, {
				method: "PUT",
				headers: await authHeaders(),
				body: JSON.stringify({
					TrainerID,
					SiteID: selectedSiteIDs,
					PermissionLevel,
					user_name,
					etp_alias,
					site_node,
					business_title,
				}),
			});

			setEditingTrainer(null);
			await refreshAll();
		} catch (error) {
			console.error("Error saving trainer changes:", error);
			alert(`Save failed: ${error.message}`);
		}
	};

	const handlePermissionLevelChange = (e) => {
		setNewPermissionLevel(e.target.value);
	};

	const handleSiteSelectionChange = (siteID) => {
		setSelectedSites((prevSelectedSites) => {
			if (prevSelectedSites.includes(siteID)) {
				return prevSelectedSites.filter((id) => id !== siteID);
			}
			return [...prevSelectedSites, siteID];
		});
	};

	const handleAddTrainer = async (e) => {
		e.preventDefault();

		if (!newTrainerID || !newTrainerName || !newPermissionLevel) {
			alert("Trainer ID, Name, and Permission Level are required.");
			return;
		}

		if (!selectedSites.length) {
			alert("Select at least one site.");
			return;
		}

		try {
			for (const siteID of selectedSites) {
				await fetchJson(`${API}/permissions`, {
					method: "POST",
					headers: await authHeaders(),
					body: JSON.stringify({
						TrainerID: newTrainerID.trim(),
						SiteID: siteID,
						PermissionLevel: newPermissionLevel,
						user_name: newTrainerName.trim(),
						etp_alias: newEtpAlias.trim() || null,
						site_node: newSiteNode.trim() || null,
						business_title: newBusinessTitle.trim() || null,
					}),
				});
			}

			setNewTrainerID("");
			setNewTrainerName("");
			setNewEtpAlias("");
			setNewBusinessTitle("");
			setNewSiteNode("");
			setSelectedSites([]);
			setNewPermissionLevel("");
			setShowSiteSelection(false);
			setSiteSearchTerm("");

			await refreshAll();
		} catch (error) {
			console.error("Error adding trainer and permissions:", error);
			alert(`Add failed: ${error.message}`);
		}
	};

	const handleDetailsClick = (targetTrainerID) => {
		const normalizedTarget = String(targetTrainerID || "")
			.trim()
			.toLowerCase();

		const trainerPermissions = permissionsData.filter(
			(permission) =>
				String(permission.TrainerID || "")
					.trim()
					.toLowerCase() === normalizedTarget,
		);

		setSelectedTrainerPermissions(trainerPermissions);
		setIsModalOpen(true);
	};
	const filteredSites = useMemo(() => {
		const term = siteSearchTerm.toLowerCase().trim();

		return (Array.isArray(sitesData) ? sitesData : []).filter((site) => {
			const siteCity = String(site.SiteCity || "").toLowerCase();
			const siteID = String(site.SiteID || "").toLowerCase();
			const siteName = String(site.SiteName || "").toLowerCase();

			return (
				!term ||
				siteCity.includes(term) ||
				siteID.includes(term) ||
				siteName.includes(term)
			);
		});
	}, [sitesData, siteSearchTerm]);

	if (isLoading) {
		return <LoadingComponent />;
	}

	return (
		<div className="container">
			<h1 className="my-4 text-center text-danger">Admin Dashboard</h1>

			<form onSubmit={handleAddTrainer} className="mb-4">
				<input
					style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)" }}
					className="mx-2"
					type="text"
					placeholder="Trainer ID"
					value={newTrainerID}
					onChange={(e) => setNewTrainerID(e.target.value)}
				/>

				<input
					style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)" }}
					className="mx-2"
					type="text"
					placeholder="Name"
					value={newTrainerName}
					onChange={(e) => setNewTrainerName(e.target.value)}
				/>

				<input
					style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)" }}
					className="mx-2"
					type="text"
					placeholder="ETP Alias (optional)"
					value={newEtpAlias}
					onChange={(e) => setNewEtpAlias(e.target.value)}
				/>

				<input
					style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)" }}
					className="mx-2"
					type="text"
					placeholder="Business Title (optional)"
					value={newBusinessTitle}
					onChange={(e) => setNewBusinessTitle(e.target.value)}
				/>

				<input
					style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)" }}
					className="mx-2"
					type="text"
					placeholder="Site Node (optional)"
					value={newSiteNode}
					onChange={(e) => setNewSiteNode(e.target.value)}
				/>

				<select
					style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)" }}
					className="mx-2"
					value={newPermissionLevel}
					onChange={handlePermissionLevelChange}
				>
					<option value="">Select Permission Level</option>
					<option value="Admin">Admin</option>
					<option value="Trainer">Trainer</option>
					<option value="Grader">Grader</option>
					<option value="Manager">Manager</option>
					<option value="Spear">Spear</option>
					<option value="HR">HR</option>
				</select>

				<button
					style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)" }}
					className="btn btn-primary mx-2"
					type="button"
					onClick={() => setShowSiteSelection(!showSiteSelection)}
				>
					{showSiteSelection ? "Hide Sites" : "Select Sites"}
				</button>

				{showSiteSelection && (
					<>
						<input
							type="text"
							placeholder="Search sites..."
							value={siteSearchTerm}
							onChange={(e) => setSiteSearchTerm(e.target.value)}
							style={{
								boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)",
								margin: "10px 0",
								padding: "5px",
								width: "100%",
							}}
						/>

						<div
							className="mb-3 mt-2"
							style={{ maxHeight: "200px", overflowY: "auto" }}
						>
							{filteredSites.map((site) => {
								const isChecked = selectedSites.includes(site.SiteID);

								return (
									<div key={site.SiteID} className="form-check">
										<input
											type="checkbox"
											className="form-check-input"
											id={`site-${site.SiteID}`}
											value={site.SiteID}
											checked={isChecked}
											onChange={() => handleSiteSelectionChange(site.SiteID)}
										/>
										<label
											className="form-check-label"
											htmlFor={`site-${site.SiteID}`}
										>
											{site.SiteName} ({site.SiteID})
										</label>
									</div>
								);
							})}
						</div>
					</>
				)}

				<button
					className="btn btn-primary mx-2"
					style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)" }}
					type="submit"
				>
					Add User
				</button>
			</form>

			<table className="table table-bordered admin-table">
				<thead>
					<tr>
						<th>Trainer ID</th>
						<th>Name</th>
						<th>ETP Alias</th>
						<th>Permission Level</th>
						<th>Action</th>
					</tr>
				</thead>
				<tbody>
					{sortedPermissions.map((trainer, index) => (
						<tr key={trainer.TrainerID}>
							<td>{trainer.TrainerID}</td>
							<td>{trainer.user_name}</td>
							<td>{trainer.etp_alias || ""}</td>
							<td>
								{Array.from(trainer.permissions)
									.map((p) => p.charAt(0).toUpperCase() + p.slice(1))
									.join(", ")}
							</td>
							<td>
								<button
									style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)" }}
									className="btn btn-primary mx-2"
									onClick={() => handleDetailsClick(trainer.TrainerID)}
								>
									Details
								</button>

								<button
									style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)" }}
									className="btn btn-secondary mx-2"
									onClick={() => handleEditClick(trainer.TrainerID)}
								>
									Edit
								</button>

								<button
									style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4)" }}
									className="btn btn-danger mx-2"
									onClick={() => handleDeleteTrainer(trainer.TrainerID)}
								>
									Delete
								</button>
							</td>
						</tr>
					))}
				</tbody>
			</table>

			<PermissionDetailsModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				trainerPermissions={selectedTrainerPermissions}
			/>

			{editingTrainer && (
				<EditTrainerModal
					isOpen={!!editingTrainer}
					onClose={() => setEditingTrainer(null)}
					trainerData={editingTrainer}
					onSave={handleSaveChanges}
					sitesData={sitesData}
				/>
			)}
		</div>
	);
};

export default AdminDashboard;
